import { Candidate } from "../interfaces/candidate";
import { CandidateCreateOrder } from "../interfaces/order";
import { fetchWithAuth } from "./fetchWithAuth";

const APP_URL = process.env.REACT_APP_APP_URL || "";
const DEV_APP_URL = process.env.REACT_APP_DEV_APP_URL || "";
const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";

const siteBaseUrl =
  window.location.hostname === "localhost"
    ? APP_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_APP_URL
      : DEV_APP_URL;

const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const createCandidate = async (
  candidateEmail: string,
): Promise<Candidate> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/Candidate/CreateNewCandidate`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(candidateEmail),
    },
  );

  if (response.ok) {
    console.log("Candidate created successfully");
    return response.json();
  } else if (response.status === 409) {
    // Handle conflict (email already in use)
    const errorData = await response.text();
    return Promise.reject(errorData);
  }

  return Promise.reject("An unexpected error occurred.");
};

export const createCandidateSuran = async (): Promise<Candidate> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/Candidate/CreateNewCandidateSuran`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (response.ok) {
    console.log("Candidate created successfully");
    return response.json();
  }

  return Promise.reject("An unexpected error occurred.");
};

export const checkEmailExists = async (email: string) => {
  const body = {
    email,
  };

  const response = await fetch(`${apiBaseUrl}/api/Candidate/CheckEmail`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();
  return data.exists;
};

export const getCandidateByEmail = async (
  candidateEmail: string,
): Promise<Candidate> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Candidate/GetCandidateByEmail/${candidateEmail}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch candidate by email");
    }

    return response.json();
  } catch (error) {
    localStorage.clear();
    window.location.href = "/login";
    throw error;
  }
};

export const getCandidateByID = async (
  candidateID: number,
): Promise<Candidate> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  try {
    const response = await fetch(
      `${apiBaseUrl}/api/Candidate/GetCandidateByID/${candidateID}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch candidate");
    }

    return response.json();
  } catch (error) {
    console.log("error fetching candidate by ID");
    throw error;
  }
};

export const checkCandidateEmail = async (
  candidateEmail: string,
): Promise<boolean> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Candidate/CheckCandidateEmail/${candidateEmail}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`API error: ${response.status} - ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error while checking candidate email:", error);
    return false;
  }
};

export const sendFormsReadyEmailToCandidate = async (
  firstName: string,
  lastName: string,
  email: string,
  diocese: string,
  clientID: number,
  orderID: number,
) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const formData = {
    firstName,
    lastName,
    email,
    diocese,
    clientID,
    orderID,
    baseUrl: `${siteBaseUrl}?portal=candidate&email=${encodeURIComponent(
      email,
    )}`,
  };

  try {
    const response = await fetch(
      `${apiBaseUrl}/api/Candidate/SendFormsReadyEmailToCandidate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    return response.ok;
  } catch (error) {
    console.error("Error sending email:", error);
    return false;
  }
};

export const alertOfInactiveClient = async (
  candidateOrder: CandidateCreateOrder,
  sendToClient: boolean,
): Promise<boolean> => {
  const formData = {
    candidateOrder,
    sendToClient,
  };

  try {
    const response = await fetch(
      `${apiBaseUrl}/api/Candidate/AlertOfInactiveClient`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Failed to send emails:", errorText);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error sending emails:", error);
    return false;
  }
};

export const checkForReferenceServicesCandidate = async (
  candidateID: number,
): Promise<boolean> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Candidate/CheckForReferenceServices`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(candidateID),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch candidate by email");
    }

    return response.json();
  } catch (error) {
    throw error;
  }
};
